/**
 * Background Task Service
 * Handles long-running operations (create/update business cases) in the background
 * with timeout management and notification system
 */

type TaskStatus = 'pending' | 'running' | 'completed' | 'failed' | 'timeout' | 'paused';

interface TaskProgress {
  taskId: string;
  type: 'create' | 'update';
  status: TaskStatus;
  currentStep: string;
  totalSteps: number;
  completedSteps: number;
  projectName: string;
  startTime: number;
  error?: string;
  result?: any;
  pausedForModal?: boolean;
}

interface TaskListener {
  (progress: TaskProgress): void;
}

interface TaskStep {
  name: string;
  fn: () => Promise<any>;
  timeout?: number;
}

interface TaskResumeCallback {
  (data?: any): void;
}

class BackgroundTaskManager {
  private tasks: Map<string, TaskProgress> = new Map();
  private listeners: Map<string, Set<TaskListener>> = new Map();
  private abortControllers: Map<string, AbortController> = new Map();
  private resumeCallbacks: Map<string, TaskResumeCallback> = new Map();
  
  private readonly DEFAULT_TIMEOUT = 120000; // 60 seconds

  /**
   * Subscribe to task progress updates
   */
  subscribe(taskId: string, listener: TaskListener): () => void {
    if (!this.listeners.has(taskId)) {
      this.listeners.set(taskId, new Set());
    }
    this.listeners.get(taskId)!.add(listener);

    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(taskId);
      if (listeners) {
        listeners.delete(listener);
        if (listeners.size === 0) {
          this.listeners.delete(taskId);
        }
      }
    };
  }

  /**
   * Notify all listeners of task progress
   */
  private notifyListeners(taskId: string, progress: TaskProgress) {
    const listeners = this.listeners.get(taskId);
    if (listeners) {
      listeners.forEach(listener => {
        try {
          listener(progress);
        } catch (error) {
          console.error('Error in task listener:', error);
        }
      });
    }
  }

  /**
   * Get task progress
   */
  getTaskProgress(taskId: string): TaskProgress | undefined {
    return this.tasks.get(taskId);
  }

  /**
   * Create a promise with timeout
   */
  private withTimeout<T>(
    promise: Promise<T>,
    timeoutMs: number,
    taskId: string
  ): Promise<T> {
    const abortController = new AbortController();
    this.abortControllers.set(taskId, abortController);

    return new Promise<T>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        abortController.abort();
        reject(new Error('Operation timed out'));
      }, timeoutMs);

      promise
        .then(result => {
          clearTimeout(timeoutId);
          this.abortControllers.delete(taskId);
          resolve(result);
        })
        .catch(error => {
          clearTimeout(timeoutId);
          this.abortControllers.delete(taskId);
          reject(error);
        });
    });
  }

  /**
   * Execute a background task
   */
  async executeTask(
    type: 'create' | 'update',
    projectName: string,
    steps: TaskStep[],
    customTaskId?: string
  ): Promise<{ taskId: string; result: any }> {
    const taskId = customTaskId || `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const progress: TaskProgress = {
      taskId,
      type,
      status: 'pending',
      currentStep: 'Initializing',
      totalSteps: steps.length,
      completedSteps: 0,
      projectName,
      startTime: Date.now(),
    };

    this.tasks.set(taskId, progress);
    this.notifyListeners(taskId, progress);

    try {
      progress.status = 'running';
      this.notifyListeners(taskId, progress);

      const results: any[] = [];

      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        progress.currentStep = step.name;
        progress.completedSteps = i;
        this.notifyListeners(taskId, progress);

        try {
          const timeout = step.timeout || this.DEFAULT_TIMEOUT;
          const result = await this.withTimeout(
            step.fn(),
            timeout,
            `${taskId}_step_${i}`
          );
          results.push(result);
        } catch (error: any) {
          if (error.message === 'Operation timed out') {
            progress.status = 'timeout';
            progress.error = `Step "${step.name}" timed out after ${(step.timeout || this.DEFAULT_TIMEOUT) / 1000}s`;
          } else {
            progress.status = 'failed';
            progress.error = error.message || `Failed at step: ${step.name}`;
          }
          this.tasks.set(taskId, progress);
          this.notifyListeners(taskId, progress);
          throw error;
        }
      }

      progress.status = 'completed';
      progress.completedSteps = steps.length;
      progress.currentStep = 'Completed';
      progress.result = results[results.length - 1]; // Last result is typically the final one
      this.tasks.set(taskId, progress);
      this.notifyListeners(taskId, progress);

      return { taskId, result: progress.result };
    } catch (error: any) {
      if (progress.status === 'running') {
        progress.status = 'failed';
        progress.error = error.message || 'Unknown error occurred';
      }
      this.tasks.set(taskId, progress);
      this.notifyListeners(taskId, progress);
      throw error;
    }
  }

  /**
   * Pause a task (e.g., waiting for user input via modal)
   */
  pauseTask(taskId: string) {
    const progress = this.tasks.get(taskId);
    if (progress && progress.status === 'running') {
      progress.status = 'paused';
      progress.pausedForModal = true;
      this.tasks.set(taskId, progress);
      this.notifyListeners(taskId, progress);
    }
  }

  /**
   * Resume a paused task
   */
  resumeTask(taskId: string, data?: any) {
    const progress = this.tasks.get(taskId);
    if (progress && progress.status === 'paused') {
      progress.status = 'running';
      progress.pausedForModal = false;
      this.tasks.set(taskId, progress);
      this.notifyListeners(taskId, progress);

      // Call resume callback if exists
      const callback = this.resumeCallbacks.get(taskId);
      if (callback) {
        callback(data);
        this.resumeCallbacks.delete(taskId);
      }
    }
  }

  /**
   * Set resume callback for a task
   */
  setResumeCallback(taskId: string, callback: TaskResumeCallback) {
    this.resumeCallbacks.set(taskId, callback);
  }

  /**
   * Cancel a running task
   */
  cancelTask(taskId: string) {
    const progress = this.tasks.get(taskId);
    if (progress && (progress.status === 'running' || progress.status === 'paused')) {
      // Abort any pending requests
      this.abortControllers.forEach((controller, key) => {
        if (key.startsWith(taskId)) {
          controller.abort();
        }
      });

      progress.status = 'failed';
      progress.error = 'Task cancelled by user';
      this.tasks.set(taskId, progress);
      this.notifyListeners(taskId, progress);
      this.resumeCallbacks.delete(taskId);
    }
  }

  /**
   * Clean up completed tasks older than specified time
   */
  cleanupOldTasks(olderThanMs: number = 3600000) { // 1 hour default
    const now = Date.now();
    this.tasks.forEach((progress, taskId) => {
      if (
        (progress.status === 'completed' || progress.status === 'failed' || progress.status === 'timeout') &&
        now - progress.startTime > olderThanMs
      ) {
        this.tasks.delete(taskId);
        this.listeners.delete(taskId);
        this.resumeCallbacks.delete(taskId);
      }
    });
  }

  /**
   * Get all active tasks
   */
  getActiveTasks(): TaskProgress[] {
    return Array.from(this.tasks.values()).filter(
      task => task.status === 'pending' || task.status === 'running' || task.status === 'paused'
    );
  }
}

// Export singleton instance
export const backgroundTaskManager = new BackgroundTaskManager();

// Export types
export type { TaskProgress, TaskStatus, TaskListener, TaskResumeCallback };
